// @react-functional

/**
 * Inspired by https://github.com/mpontus/react-modal-hook
 * But without the close on parent's unmount business logic because we open modales from popovers
 */

import { useMemo, useContext } from 'react';
import { ModalContext, ModalType, ModalContextType } from './ModalContext';

/**
 * Check whether the argument is a stateless component.
 *
 * We take advantage of the stateless nature of functional components to be
 * inline the rendering of the modal component as part of another immutable
 * component.
 *
 * This is necessary for allowing the modal to update based on the inputs passed
 * as the second argument to useModal without unmounting the previous version of
 * the modal component.
 */
const isFunctionComponent = (Component: Function) => {
  const { prototype } = Component;

  return !prototype || !prototype.isReactComponent;
};

export const useModal = <CustomProps,>(
  component: ModalType<CustomProps>,
  inputs: any[] = [],
) => {
  if (!isFunctionComponent(component)) {
    throw new Error(
      'Only function components can be used as an argument to useModal. You have probably passed a class component where a function was expected.',
    );
  }

  const modal = useMemo(() => component, inputs);
  const context = useContext<ModalContextType<CustomProps>>(ModalContext);

  const openModal = (customProps?: CustomProps) => {
    context.openModal(modal, customProps);
  };

  const closeModal = () => {
    context.closeModal(modal);
  };

  return { openModal, closeModal };
};
